import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import Icon from '../../components/AppIcon';
import DateRangeFilter from '../analytics-dashboard/components/DateRangeFilter';
import ReconciliationSummary from './components/ReconciliationSummary';
import TransactionMatching from './components/TransactionMatching';
import CashFlowAnalysis from './components/CashFlowAnalysis';
import AuditTrail from './components/AuditTrail';
import IntegrationPanel from './components/IntegrationPanel';
import TaxReporting from './components/TaxReporting';

const FinancialReconciliationDashboard = () => {
  const { userProfile } = useAuth();
  const userRole = userProfile?.role || 'manager';
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  const [viewMode, setViewMode] = useState('overview');

  const reconciliationSummary = {
    daily: {
      posTransactions: 12450,
      membershipPayments: 8900,
      bankDeposits: 21350,
      discrepancies: 0,
      reconciliationRate: 100,
      lastUpdated: '2 minutes ago'
    },
    weekly: {
      posTransactions: 87150,
      membershipPayments: 62300,
      bankDeposits: 149450,
      discrepancies: 0,
      reconciliationRate: 100,
      lastUpdated: '1 hour ago'
    },
    monthly: {
      posTransactions: 348600,
      membershipPayments: 249200,
      bankDeposits: 597800,
      discrepancies: 0,
      reconciliationRate: 100,
      lastUpdated: '3 hours ago'
    }
  };

  const unmatchedTransactions = [
    {
      id: 'TXN-2024-001',
      date: '2024-01-25',
      type: 'POS',
      amount: 125.50,
      description: 'Retail Sale - Protein Powder',
      status: 'pending_review',
      paymentMethod: 'Credit Card',
      member: 'John Anderson'
    },
    {
      id: 'TXN-2024-002',
      date: '2024-01-25',
      type: 'Membership',
      amount: 79.00,
      description: 'Monthly Membership - Standard Plan',
      status: 'payment_failed',
      paymentMethod: 'Auto-debit',
      member: 'Sarah Johnson'
    },
    {
      id: 'TXN-2024-003',
      date: '2024-01-24',
      type: 'Bank Deposit',
      amount: 1500.00,
      description: 'Cash Deposit - Front Desk',
      status: 'unmatched',
      paymentMethod: 'Cash',
      member: 'N/A'
    }
  ];

  const cashFlowData = {
    accountsReceivable: 24580,
    outstandingBalances: 8920,
    paymentFailures: 12,
    retryScheduled: 8,
    collections: [
      { period: 'Current', amount: 15660, percentage: 64 },
      { period: '1-30 Days', amount: 5340, percentage: 22 },
      { period: '31-60 Days', amount: 2460, percentage: 10 },
      { period: '60+ Days', amount: 1120, percentage: 4 }
    ]
  };

  const auditTrailData = [
    {
      id: 'AUD-001',
      timestamp: '2024-01-25 14:32:15',
      user: 'Sarah Johnson',
      userRole: 'Manager',
      action: 'Manual Adjustment',
      description: 'Corrected POS transaction amount from $125.00 to $125.50',
      transactionId: 'TXN-2024-001',
      previousValue: '$125.00',
      newValue: '$125.50',
      approvalStatus: 'Approved'
    },
    {
      id: 'AUD-002',
      timestamp: '2024-01-25 13:15:42',
      user: 'Michael Chen',
      userRole: 'Owner',
      action: 'Reconciliation Override',
      description: 'Marked bank deposit as reconciled after verification',
      transactionId: 'TXN-2024-003',
      previousValue: 'Unmatched',
      newValue: 'Reconciled',
      approvalStatus: 'Approved'
    },
    {
      id: 'AUD-003',
      timestamp: '2024-01-25 11:45:23',
      user: 'Emily Rodriguez',
      userRole: 'Staff',
      action: 'Payment Retry',
      description: 'Initiated payment retry for failed membership transaction',
      transactionId: 'TXN-2024-002',
      previousValue: 'Failed',
      newValue: 'Retry Scheduled',
      approvalStatus: 'Pending'
    }
  ];

  const integrations = [
    {
      name: 'QuickBooks',
      status: 'connected',
      lastSync: '5 minutes ago',
      icon: 'FileText',
      syncStatus: 'success',
      recordsSynced: 1247
    },
    {
      name: 'Stripe',
      status: 'connected',
      lastSync: '2 minutes ago',
      icon: 'CreditCard',
      syncStatus: 'success',
      recordsSynced: 342
    },
    {
      name: 'Bank API',
      status: 'connected',
      lastSync: '10 minutes ago',
      icon: 'Building',
      syncStatus: 'success',
      recordsSynced: 89
    },
    {
      name: 'PayPal',
      status: 'disconnected',
      lastSync: 'Never',
      icon: 'Wallet',
      syncStatus: 'error',
      recordsSynced: 0
    }
  ];

  const taxReportingData = {
    salesTax: {
      collected: 3240,
      rate: 8.5,
      period: 'January 2024',
      dueDate: '2024-02-15',
      status: 'pending'
    },
    payrollTax: {
      withheld: 12450,
      period: 'January 2024',
      dueDate: '2024-02-15',
      status: 'pending'
    },
    businessExpenses: [
      { category: 'Equipment', amount: 8500, deductible: true },
      { category: 'Utilities', amount: 2340, deductible: true },
      { category: 'Marketing', amount: 1850, deductible: true },
      { category: 'Supplies', amount: 980, deductible: true }
    ]
  };

  const periodOptions = [
    { value: 'daily', label: 'Daily', icon: 'Calendar' },
    { value: 'weekly', label: 'Weekly', icon: 'CalendarDays' },
    { value: 'monthly', label: 'Monthly', icon: 'CalendarRange' }
  ];

  const handleDateRangeChange = (range) => {
    console.log('Date range changed:', range);
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-3">
            Financial Reconciliation Dashboard
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">
            Automated financial verification and accounting integration
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-2">
            {periodOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => setSelectedPeriod(option?.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 ${
                  selectedPeriod === option?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={option?.icon} size={16} />
                {option?.label}
              </button>
            ))}
          </div>
          <DateRangeFilter onFilterChange={handleDateRangeChange} />
        </div>

        <div className="mb-6 md:mb-8">
          <ReconciliationSummary data={reconciliationSummary?.[selectedPeriod]} period={selectedPeriod} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <TransactionMatching transactions={unmatchedTransactions} />
          <CashFlowAnalysis data={cashFlowData} />
        </div>

        <div className="mb-6 md:mb-8">
          <IntegrationPanel integrations={integrations} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <AuditTrail data={auditTrailData} />
          <TaxReporting data={taxReportingData} />
        </div>
      </main>
    </div>
  );
};

export default FinancialReconciliationDashboard;